import type {
  TDependent,
  TDependentInvitation,
} from '@cascade/api-client/dependents';
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useApi } from '#providers/api/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import { getQueryLoadState } from '#providers/query/index.ts';
import { DependentsContext } from './Dependents.context.ts';
import {
  getDependentsQueryKey,
  hasDependentEmail,
  requireSuccess,
} from './Dependents.helpers.ts';
import type {
  TAddDependentPayload,
  TDependentsContextValue,
  TDependentsProviderProps,
} from './Dependents.types.ts';

const DEPENDENT_LIMIT = 3;
const noDependents: TDependent[] = [];
const noInvitations: TDependentInvitation[] = [];

export const DependentsProvider = ({ children }: TDependentsProviderProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { currentProjectId, canManageDependents, requireProjectId } =
    useProjects();

  const dependentsKey = useMemo(
    () => getDependentsQueryKey(currentProjectId),
    [currentProjectId],
  );

  const dependentsQuery = useQuery({
    queryKey: dependentsKey,
    queryFn:
      currentProjectId && canManageDependents
        ? () => api.dependents.listDependents({ project_id: currentProjectId })
        : skipToken,
  });

  const activeDependents =
    dependentsQuery.data?.active_dependents ?? noDependents;

  const pendingInvitations =
    dependentsQuery.data?.pending_invitations ?? noInvitations;

  const { isLoading: isLoadingDependents, loadError: dependentsError } =
    getQueryLoadState(dependentsQuery);

  const hasReachedDependentLimit =
    activeDependents.length + pendingInvitations.length >= DEPENDENT_LIMIT;

  const invalidateDependents = useCallback(
    () => queryClient.invalidateQueries({ queryKey: dependentsKey }),
    [queryClient, dependentsKey],
  );

  const hasDependent = useCallback(
    (email: string) => {
      const emails = [...activeDependents, ...pendingInvitations].map(
        (dependent) => dependent.email,
      );

      return hasDependentEmail(emails, email);
    },
    [activeDependents, pendingInvitations],
  );

  const { mutateAsync: addDependent, isPending: isAddingDependent } =
    useMutation({
      mutationFn: async (payload: TAddDependentPayload) => {
        const response = await api.dependents.addDependent({
          ...payload,
          project_id: requireProjectId(),
        });

        return requireSuccess(response);
      },
      onSuccess: invalidateDependents,
    });

  const { mutateAsync: removeDependent, isPending: isRemovingDependent } =
    useMutation({
      mutationFn: async (email: string) => {
        const response = await api.dependents.removeDependent({
          email,
          project_id: requireProjectId(),
        });

        return requireSuccess(response);
      },
      onSuccess: invalidateDependents,
    });

  const value = useMemo<TDependentsContextValue>(
    () => ({
      activeDependents,
      pendingInvitations,
      isLoadingDependents,
      dependentsError,
      hasReachedDependentLimit,
      isAddingDependent,
      isRemovingDependent,
      hasDependent,
      addDependent,
      removeDependent,
    }),
    [
      activeDependents,
      pendingInvitations,
      isLoadingDependents,
      dependentsError,
      hasReachedDependentLimit,
      isAddingDependent,
      isRemovingDependent,
      hasDependent,
      addDependent,
      removeDependent,
    ],
  );

  return (
    <DependentsContext.Provider value={value}>
      {children}
    </DependentsContext.Provider>
  );
};
